<template>
    <BaseTemplate app-name="Gestão de Ativos" page-title="">
        <template #content>
  <div class="container mx-auto px-4 py-4 md:py-8">
      <div class="flex flex-col  md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div class="flex flex-col sm:flex-row gap-2 w-full">
          <input v-model="localFilters.performed_by" type="text" placeholder="Filtrar por colaborador" class="w-full rounded-lg sm:w-48 px-3 py-2 border rounded shadow focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          <select v-model="localFilters.resource" class="w-full rounded-lg sm:w-48 px-3 py-2 border rounded shadow focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
            <option value="">Todos os recursos</option>
            <option v-for="recurso in tiposRecursos" :key="recurso" :value="recurso">{{ formatResourceName(recurso) }}</option>
          </select>
          <select v-model="localFilters.action" class="w-full rounded-lg sm:w-48 px-3 py-2 border rounded shadow focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
            <option value="">Todas as ações</option>
            <option v-for="tipo in tiposAcoes" :key="tipo" :value="tipo">{{ formatActionName(tipo) }}</option>
          </select>
        </div>
        <!-- Botão de Gerar Relatório -->
        <div class="flex gap-2">
          <button @click="showReportModal = true" class="px-4 py-2 clinic-btn text-white rounded-lg transition-colors duration-200 flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            Relatório
          </button>
        </div>
      </div>

      <!-- Tabela de Auditoria (Desktop) -->
      <div class="hidden md:block overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow">
          <table class="min-w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700">
              <thead class="clinic-table-header text-sm uppercase tracking-wide">
                  <tr>
                      <th v-for="header in ['ID', 'Recurso', 'Ação', 'Colaborador', 'Detalhes', 'Feito em']" :key="header" class="py-4 px-6 w-1/9 text-left">
                          {{ header }}
                      </th>
                  </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                  <tr v-for="auditoria in auditoria" :key="auditoria.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <!-- Coluna ID -->
                      <td class="py-4 px-6 text-sm font-medium text-gray-900 dark:text-white">
                          {{ auditoria.id }}
                      </td>
                      <!-- Coluna Recurso -->
                      <td class="py-4 px-6 text-sm text-gray-500 dark:text-gray-400 uppercase">
                          {{ formatResourceName(auditoria.resource) || '-' }}
                      </td>
                      <!-- Coluna Ação -->
                      <td class="py-4 px-6 text-sm text-gray-500 dark:text-gray-400 uppercase">
                          {{ formatActionName(auditoria.action) || '-' }}
                      </td>
                      <!-- Coluna Colaborador -->
                      <td class="py-4 px-6 text-sm text-gray-500 dark:text-gray-400 capitalize">
                          {{ auditoria.performed_by || '-' }}
                      </td>
                      <!-- Coluna Detalhes -->
                      <td class="py-4 px-6 text-sm text-gray-500 dark:text-gray-400">
                          {{ auditoria.details || '-' }}
                      </td>
                      <!-- Coluna Timestamp -->
                      <td class="py-4 px-6 text-sm text-gray-500 dark:text-gray-400">
                          {{ formatarData(auditoria.timestamp) }}
                      </td>
                  </tr>
              </tbody>
          </table>
      </div>

      <!-- Cards de Auditoria (Mobile) -->
      <div class="block md:hidden space-y-4">
          <div v-for="auditoria in auditoria" :key="auditoria.id" class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <!-- ID -->
              <div class="flex items-center space-x-2">
                  <span class="text-sm font-semibold text-gray-700 dark:text-gray-300">ID:</span>
                  <span class="text-sm text-gray-500 dark:text-gray-400">{{ auditoria.id }}</span>
              </div>
              <!-- Recurso -->
              <div class="flex items-center space-x-2">
                  <span class="text-sm font-semibold text-gray-700 dark:text-gray-300">Recurso:</span>
                  <span class="text-sm text-gray-500 dark:text-gray-400 uppercase">{{ formatResourceName(auditoria.resource) || '-' }}</span>
              </div>
              <!-- Ação -->
              <div class="flex items-center space-x-2">
                  <span class="text-sm font-semibold text-gray-700 dark:text-gray-300">Ação:</span>
                  <span class="text-sm text-gray-500 dark:text-gray-400 uppercase">{{ formatActionName(auditoria.action) || '-' }}</span>
              </div>
              <!-- Colaborador -->
              <div class="flex items-center space-x-2">
                  <span class="text-sm font-semibold text-gray-700 dark:text-gray-300">Colaborador:</span>
                  <span class="text-sm text-gray-500 dark:text-gray-400 capitalize">{{ auditoria.performed_by || '-' }}</span>
              </div>
              <!-- Detalhes -->
              <div class="flex items-center space-x-2">
                  <span class="text-sm font-semibold text-gray-700 dark:text-gray-300">Detalhes:</span>
                  <span class="text-sm text-gray-500 dark:text-gray-400">{{ auditoria.details || '-' }}</span>
              </div>
              <!-- Feito em -->
              <div class="flex items-center space-x-2">
                  <span class="text-sm font-semibold text-gray-700 dark:text-gray-300">Feito em:</span>
                  <span class="text-sm text-gray-500 dark:text-gray-400">{{ formatarData(auditoria.timestamp) }}</span>
              </div>
          </div>
      </div>
    <!-- Paginação -->
    <div class="w-full mt-6">
      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <!-- Esquerda: Página e seletor -->
        <div class="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto justify-center md:justify-start">
          <span class="text-sm text-gray-600 dark:text-gray-300 text-center md:text-left">Página {{ currentPage }} de {{ totalPages }}</span>
          <label class="flex items-center gap-2 justify-center md:justify-start">
            <span class="text-xs text-gray-500 dark:text-gray-400">Itens por página:</span>
            <select v-model.number="pageSize" @change="onPageSizeChange" class="w-28 px-2 py-1 border rounded shadow focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
              <option v-for="size in [10, 15, 25, 50, 100]" :key="size" :value="size">{{ size }}</option>
            </select>
          </label>
        </div>
        <!-- Direita: Botões de navegação -->
        <div class="flex gap-2 justify-center md:justify-end w-full md:w-auto">
          <button @click="prevPage" :disabled="currentPage === 1" class="px-4 py-2 rounded clinic-btn disabled:opacity-50 transition-colors">Anterior</button>
          <button @click="nextPage" :disabled="currentPage === totalPages" class="px-4 py-2 rounded clinic-btn disabled:opacity-50 transition-colors">Próxima</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal de Relatório -->
  <div v-if="showReportModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <!-- Header do Modal -->
      <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Gerar Relatório de Auditoria</h3>
        <button @click="showReportModal = false" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Conteúdo do Modal -->
      <div class="p-6 space-y-6">
        <!-- Filtros do Relatório -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Recurso</label>
            <select v-model="reportForm.resource" class="w-full border-0 bg-gray-50 dark:bg-gray-900 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 text-gray-800 dark:text-gray-100">
              <option value="">Todos os Recursos</option>
              <option v-for="recurso in tiposRecursos" :key="recurso" :value="recurso">{{ formatResourceName(recurso) }}</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Ação</label>
            <select v-model="reportForm.action" class="w-full border-0 bg-gray-50 dark:bg-gray-900 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 text-gray-800 dark:text-gray-100">
              <option value="">Todas as Ações</option>
              <option v-for="tipo in tiposAcoes" :key="tipo" :value="tipo">{{ formatActionName(tipo) }}</option>
            </select>
          </div>
        </div>

        <!-- Período -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Período</label>
          <div class="flex items-center gap-2">
            <input type="date" v-model="reportForm.data_inicio" class="flex-1 border-0 bg-gray-50 dark:bg-gray-900 rounded-xl px-4 py-2 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-800" />
            <span class="text-gray-500 dark:text-gray-400">a</span>
            <input type="date" v-model="reportForm.data_fim" class="flex-1 border-0 bg-gray-50 dark:bg-gray-900 rounded-xl px-4 py-2 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-800" />
          </div>
        </div>

        <!-- Configurações de Formato -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Tipo de Emissão</label>
            <div class="flex gap-4">
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="radio" v-model="reportForm.tipo_emissao" value="analitico" class="w-4 h-4 clinic-radio" />
                <span class="text-sm text-gray-700 dark:text-gray-200">Analítico</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="radio" v-model="reportForm.tipo_emissao" value="sintetico" class="w-4 h-4 clinic-radio" />
                <span class="text-sm text-gray-700 dark:text-gray-200">Sintético</span>
              </label>
            </div>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Orientação do PDF</label>
            <div class="flex gap-4">
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="radio" v-model="reportForm.orientation" value="portrait" class="w-4 h-4 clinic-radio" />
                <span class="text-sm text-gray-700 dark:text-gray-200">Vertical</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="radio" v-model="reportForm.orientation" value="landscape" class="w-4 h-4 clinic-radio" />
                <span class="text-sm text-gray-700 dark:text-gray-200">Horizontal</span>
              </label>
            </div>
          </div>
        </div>

        <!-- Botões de Ação -->
        <div class="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <button @click="showReportModal = false" class="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-colors">
            Cancelar
          </button>
          <button @click="generateReport" :disabled="isGeneratingReport" class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2 disabled:opacity-50">
            <svg v-if="isGeneratingReport" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
            <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
            </svg>
            {{ isGeneratingReport ? 'Gerando...' : 'Abrir Relatório' }}
          </button>
        </div>
      </div>
    </div>
  </div>
        </template>
    </BaseTemplate>
</template>

<script setup>
import  BaseTemplate from '../templates/BaseTemplate.vue';
import { ref, watch, onMounted } from 'vue';
import apiClient from '@/services/authService';
import { useClinicStore } from '@/stores/settings/clinicStore';

const auditoria = ref([]);
const total = ref(0);
const totalPages = ref(1);
const pageSize = ref(15);
const showReportModal = ref(false);
const isGeneratingReport = ref(false);

// Formulário do relatório
const reportForm = ref({
  resource: '',
  action: '',
  data_inicio: '',
  data_fim: '',
  tipo_emissao: 'analitico',
  orientation: 'portrait',
});

function onPageSizeChange() {
  currentPage.value = 1;
  fetchAuditoria();
}
const currentPage = ref(1);

const localFilters = ref({ performed_by: '', resource: '', action: '' });

async function fetchAuditoria() {
  try {
    const params = {
      page: currentPage.value,
      page_size: pageSize.value,
      performed_by: localFilters.value.performed_by || undefined,
      resource: localFilters.value.resource || undefined,
      action: localFilters.value.action || undefined,
    };
    const { data } = await apiClient.get('/api/v1/audit/', { params });
    auditoria.value = data.items || [];
    total.value = data.total || 0;
    totalPages.value = data.total_pages || 1;
    pageSize.value = data.page_size || 15;
    currentPage.value = data.page || 1;
  } catch (error) {
    auditoria.value = [];
    total.value = 0;
    totalPages.value = 1;
    console.error("Erro ao buscar auditoria:", error);
  }
}

const clinicStore = useClinicStore();
clinicStore.initialize();

const tiposAcoes = ref([]);
const tiposRecursos = ref([]);

async function fetchTiposAcoes() {
  try {
    const { data } = await apiClient.get('/api/v1/audit/actions/');
    tiposAcoes.value = Array.isArray(data) ? data : [];
  } catch (error) {
    tiposAcoes.value = [];
    console.error('Erro ao buscar tipos de ação:', error);
  }
}

async function fetchTiposRecursos() {
  try {
    const { data } = await apiClient.get('/api/v1/audit/resources/');
    tiposRecursos.value = Array.isArray(data) ? data : [];
  } catch (error) {
    tiposRecursos.value = [];
    console.error('Erro ao buscar tipos de recursos:', error);
  }
}

function nextPage() {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
    fetchAuditoria();
  }
}
function prevPage() {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetchAuditoria();
  }
}

watch([() => localFilters.value.performed_by, () => localFilters.value.resource, () => localFilters.value.action], () => {
  currentPage.value = 1;
  fetchAuditoria();
});

const formatarData = (data) => {
    if (!data) return "Sem data";
    return new Date(data).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });
};

// Funções de formatação para português
const formatResourceName = (resource) => {
    if (!resource) return '';
    const resourceNames = {
        'servico': 'Serviço',
        'ativo': 'Ativo',
        'nota': 'Nota',
        'kit': 'Kit',
        'movimentacao': 'Movimentação',
        'manutencao': 'Manutenção',
        'schedule': 'Agenda',
        'appointment': 'Agendamento',
        'sisreg_import': 'Importação SISREG',
        'document': 'Documento',
        'worklist': 'Worklist',
        'anamnesis': 'Anamnese',
        'editexam': 'Edição de Exame PACS',
        'not_performed': 'Não Realizado',
        'notificacao': 'Notificação',
        'user': 'Usuário',
        'profile': 'Perfil',
        'report': 'Relatório',
        'kpi': 'KPI',
        'metric': 'Métrica',
        'consultation': 'Consulta',
        'medical_report': 'Laudo Médico',
        'patient': 'Paciente',
        'doctor': 'Médico',
        'room': 'Sala',
        'insurance': 'Convênio',
        'exam_delivery': 'Entrega de Exame',
        'mask_laudo': 'Máscara de Laudo',
        'clinic_settings': 'Configurações da Clínica',
        'special_slot': 'Horário Especial'
    };
    return resourceNames[resource] || resource;
};

const formatActionName = (action) => {
    if (!action) return '';
    const actionNames = {
        'create': 'Criar',
        'update': 'Atualizar',
        'delete': 'Excluir',
        'merge': 'Juntar',
        'view': 'Visualizar',
        'export': 'Exportar',
        'import': 'Importar',
        'upload': 'Enviar',
        'download': 'Baixar',
        'generate': 'Gerar',
        'status_update': 'Atualizar Status',
        'update service': 'Atualizar Serviço',
        'login': 'Login',
        'logout': 'Logout',
        'aceite termo': 'Aceite de Termo',
        'recusa termo': 'Recusa de Termo',
        'reconvocou paciente': 'Reconvocação de Paciente',
        'edit': 'Editar',
        'save': 'Salvar',
        'cancel': 'Cancelar',
        'approve': 'Aprovar',
        'reject': 'Rejeitar',
        'activate': 'Ativar',
        'deactivate': 'Desativar',
        'archive': 'Arquivar',
        'restore': 'Restaurar',
        'duplicate': 'Duplicar',
        'move': 'Mover',
        'copy': 'Copiar',
        'print': 'Imprimir',
        'email': 'Enviar Email',
        'notify': 'Notificar',
        'assign': 'Atribuir',
        'unassign': 'Desatribuir',
        'start': 'Iniciar',
        'stop': 'Parar',
        'pause': 'Pausar',
        'resume': 'Retomar',
        'complete': 'Completar',
        'finish': 'Finalizar',
        'close': 'Fechar',
        'open': 'Abrir',
        'lock': 'Bloquear',
        'unlock': 'Desbloquear',
        'verify': 'Verificar',
        'validate': 'Validar',
        'process': 'Processar',
        'execute': 'Executar',
        'run': 'Executar',
        'schedule': 'Agendar',
        'reschedule': 'Reagendar',
        'cancel_appointment': 'Cancelar Agendamento',
        'confirm_appointment': 'Confirmar Agendamento',
        'check_in': 'Check-in',
        'check_out': 'Check-out',
        'register': 'Registrar',
        'unregister': 'Desregistrar',
        'subscribe': 'Inscrever',
        'unsubscribe': 'Desinscrever',
        'enable': 'Habilitar',
        'disable': 'Desabilitar',
        'configure': 'Configurar',
        'setup': 'Configurar',
        'install': 'Instalar',
        'uninstall': 'Desinstalar',
        'upgrade': 'Atualizar',
        'downgrade': 'Rebaixar',
        'backup': 'Backup',
        'restore_backup': 'Restaurar Backup',
        'sync': 'Sincronizar',
        'refresh': 'Atualizar',
        'reload': 'Recarregar',
        'reset': 'Resetar',
        'clear': 'Limpar',
        'empty': 'Esvaziar',
        'fill': 'Preencher',
        'empty_trash': 'Esvaziar Lixeira',
        'restore_from_trash': 'Restaurar da Lixeira',
        'permanent_delete': 'Exclusão Permanente',
        'soft_delete': 'Exclusão Temporária',
        'hard_delete': 'Exclusão Definitiva',
        'bulk_delete': 'Exclusão em Massa',
        'bulk_update': 'Atualização em Massa',
        'bulk_create': 'Criação em Massa',
        'bulk_import': 'Importação em Massa',
        'bulk_export': 'Exportação em Massa',
        'mass_update': 'Atualização em Massa',
        'batch_process': 'Processamento em Lote',
        'queue': 'Enfileirar',
        'dequeue': 'Desenfileirar',
        'prioritize': 'Priorizar',
        'deprioritize': 'Despriorizar',
        'flag': 'Marcar',
        'unflag': 'Desmarcar',
        'star': 'Favoritar',
        'unstar': 'Desfavoritar',
        'bookmark': 'Favoritar',
        'unbookmark': 'Desfavoritar',
        'pin': 'Fixar',
        'unpin': 'Desfixar',
        'highlight': 'Destacar',
        'unhighlight': 'Remover Destaque',
        'tag': 'Marcar',
        'untag': 'Desmarcar',
        'label': 'Rotular',
        'unlabel': 'Remover Rótulo',
        'categorize': 'Categorizar',
        'uncategorize': 'Des categorizar',
        'classify': 'Classificar',
        'unclassify': 'Desclassificar',
        'sort': 'Ordenar',
        'filter': 'Filtrar',
        'search': 'Pesquisar',
        'find': 'Encontrar',
        'locate': 'Localizar',
        'track': 'Rastrear',
        'untrack': 'Parar Rastreamento',
        'monitor': 'Monitorar',
        'unmonitor': 'Parar Monitoramento',
        'watch': 'Observar',
        'unwatch': 'Parar Observação',
        'follow': 'Seguir',
        'unfollow': 'Deixar de Seguir',
        'subscribe_to_updates': 'Inscrever para Atualizações',
        'unsubscribe_from_updates': 'Desinscrever de Atualizações',
        'opt_in': 'Optar por',
        'opt_out': 'Optar por Não',
        'consent': 'Consentir',
        'withdraw_consent': 'Retirar Consentimento',
        'accept_terms': 'Aceitar Termos',
        'decline_terms': 'Recusar Termos',
        'sign': 'Assinar',
        'unsign': 'Desassinar',
        'approve_request': 'Aprovar Solicitação',
        'reject_request': 'Rejeitar Solicitação',
        'grant_permission': 'Conceder Permissão',
        'revoke_permission': 'Revogar Permissão',
        'assign_role': 'Atribuir Função',
        'remove_role': 'Remover Função',
        'promote': 'Promover',
        'demote': 'Rebaixar',
        'activate_account': 'Ativar Conta',
        'deactivate_account': 'Desativar Conta',
        'suspend_account': 'Suspender Conta',
        'unsuspend_account': 'Remover Suspensão da Conta',
        'block_user': 'Bloquear Usuário',
        'unblock_user': 'Desbloquear Usuário',
        'ban_user': 'Banir Usuário',
        'unban_user': 'Desbanir Usuário',
        'mute_user': 'Silenciar Usuário',
        'unmute_user': 'Remover Silêncio do Usuário',
        'warn_user': 'Advertir Usuário',
        'forgive_user': 'Perdoar Usuário',
        'reset_password': 'Resetar Senha',
        'change_password': 'Alterar Senha',
        'set_password': 'Definir Senha',
        'unlock_account': 'Desbloquear Conta',
        'lock_account': 'Bloquear Conta',
        'verify_email': 'Verificar Email',
        'confirm_email': 'Confirmar Email',
        'resend_verification': 'Reenviar Verificação',
        'request_password_reset': 'Solicitar Reset de Senha',
        'cancel_password_reset': 'Cancelar Reset de Senha',
        'complete_registration': 'Completar Registro',
        'cancel_registration': 'Cancelar Registro',
        'activate_subscription': 'Ativar Assinatura',
        'deactivate_subscription': 'Desativar Assinatura',
        'cancel_subscription': 'Cancelar Assinatura',
        'renew_subscription': 'Renovar Assinatura',
        'upgrade_subscription': 'Atualizar Assinatura',
        'downgrade_subscription': 'Rebaixar Assinatura',
        'pause_subscription': 'Pausar Assinatura',
        'resume_subscription': 'Retomar Assinatura',
        'extend_subscription': 'Estender Assinatura',
        'shorten_subscription': 'Encurtar Assinatura',
        'modify_subscription': 'Modificar Assinatura',
        'transfer_subscription': 'Transferir Assinatura',
        'gift_subscription': 'Presentear Assinatura',
        'accept_gift': 'Aceitar Presente',
        'decline_gift': 'Recusar Presente',
        'return_gift': 'Devolver Presente',
        'exchange_gift': 'Trocar Presente',
        'refund': 'Reembolsar',
        'partial_refund': 'Reembolso Parcial',
        'deny_refund': 'Negar Reembolso',
        'approve_refund': 'Aprovar Reembolso',
        'process_refund': 'Processar Reembolso',
        'cancel_refund': 'Cancelar Reembolso',
        'issue_credit': 'Emitir Crédito',
        'apply_credit': 'Aplicar Crédito',
        'expire_credit': 'Expirar Crédito',
        'extend_credit': 'Estender Crédito',
        'reduce_credit': 'Reduzir Crédito',
        'add_credit': 'Adicionar Crédito',
        'subtract_credit': 'Subtrair Crédito',
        'balance_credit': 'Balancear Crédito',
        'transfer_credit': 'Transferir Crédito',
        'gift_credit': 'Presentear Crédito',
        'accept_credit': 'Aceitar Crédito',
        'decline_credit': 'Recusar Crédito',
        'return_credit': 'Devolver Crédito',
        'exchange_credit': 'Trocar Crédito',
        'convert_credit': 'Converter Crédito',
        'split_credit': 'Dividir Crédito',
        'merge_credit': 'Mesclar Crédito',
        'consolidate_credit': 'Consolidar Crédito',
        'distribute_credit': 'Distribuir Crédito',
        'allocate_credit': 'Alocar Crédito',
        'deallocate_credit': 'Desalocar Crédito',
        'reserve_credit': 'Reservar Crédito',
        'release_credit': 'Liberar Crédito',
        'hold_credit': 'Retener Crédito',
        'unhold_credit': 'Remover Retenção de Crédito',
        'freeze_credit': 'Congelar Crédito',
        'unfreeze_credit': 'Descongelar Crédito',
        'lock_credit': 'Bloquear Crédito',
        'unlock_credit': 'Desbloquear Crédito',
        'suspend_credit': 'Suspender Crédito',
        'unsuspend_credit': 'Remover Suspensão de Crédito',
        'terminate_credit': 'Terminar Crédito',
        'reactivate_credit': 'Reativar Crédito',
        'archive_credit': 'Arquivar Crédito',
        'unarchive_credit': 'Desarquivar Crédito',
        'delete_credit': 'Excluir Crédito',
        'restore_credit': 'Restaurar Crédito',
        'duplicate_credit': 'Duplicar Crédito',
        'copy_credit': 'Copiar Crédito',
        'move_credit': 'Mover Crédito',
        'rename_credit': 'Renomear Crédito',
        'reclassify_credit': 'Reclassificar Crédito',
        'reassign_credit': 'Reatribuir Crédito',
        'redistribute_credit': 'Redistribuir Crédito',
        'reallocate_credit': 'Realocar Crédito',
        'rebalance_credit': 'Rebalancear Crédito',
        'reconcile_credit': 'Reconciliar Crédito',
        'audit_credit': 'Auditar Crédito',
        'review_credit': 'Revisar Crédito',
        'approve_credit': 'Aprovar Crédito',
        'reject_credit': 'Rejeitar Crédito',
        'validate_credit': 'Validar Crédito',
        'verify_credit': 'Verificar Crédito',
        'confirm_credit': 'Confirmar Crédito',
        'acknowledge_credit': 'Reconhecer Crédito'
    };
    return actionNames[action] || action;
};

// Função para gerar relatório
const generateReport = async () => {
    try {
        isGeneratingReport.value = true;
        
        const reportRequestData = {
            type: 'auditoria',
            filters: {
                resource: reportForm.value.resource || null,
                action: reportForm.value.action || null,
                data_inicio: reportForm.value.data_inicio || null,
                data_fim: reportForm.value.data_fim || null,
            },
            tipo_emissao: reportForm.value.tipo_emissao,
            orientation: reportForm.value.orientation,
        };
        
        // Chamar API para gerar PDF
        const response = await apiClient.post('/api/v1/reports/custom-reports/preview-pdf/', reportRequestData, {
            responseType: 'blob'
        });
        
        // Abrir PDF em nova aba
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        
        // Fechar modal
        showReportModal.value = false;
        
        // Limpar formulário
        reportForm.value = {
            resource: '',
            action: '',
            data_inicio: '',
            data_fim: '',
            tipo_emissao: 'analitico',
            orientation: 'portrait',
        };
        
    } catch (error) {
        console.error('Erro ao gerar relatório:', error);
        alert('Erro ao gerar relatório. Tente novamente.');
    } finally {
        isGeneratingReport.value = false;
    }
};

onMounted(() => {
    fetchAuditoria();
    fetchTiposAcoes();
    fetchTiposRecursos();
});
</script>

<style scoped>
.clinic-btn {
  background: var(--clinic-btn-bg) !important;
  color: #fff !important;
  transition: background 0.2s;
}
.clinic-btn:hover, .clinic-btn:focus {
  background: var(--clinic-btn-bg-hover) !important;
}
.clinic-header {
  color: var(--clinic-font-color) !important;
}
.clinic-table-header {
  background: var(--clinic-btn-bg) !important;
  color: var(--clinic-font-color) !important;
}
.clinic-radio {
  accent-color: var(--clinic-btn-bg) !important;
}
.clinic-radio:checked {
  accent-color: var(--clinic-btn-bg) !important;
}
</style>